import { SalesReportError } from '@/components/money/SalesReportError'
import ReportDateRangeFilterBar, {
  type ReportDateRangeSelection,
} from '@/components/reports/ReportDateRangeFilterBar'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { LoadingState } from '@/components/patterns/loading-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePathHelper } from '@/hooks/usePathHelper'
import {
  REPORT_DATE_MODE,
  getCurrentReportDateFilters,
  getOrganizationReportDateBounds,
} from '@/lib/report-date-range'
import { canDisplaySalesReport } from '@/lib/services/money/money.query'
import {
  organizationQueries,
  useOrganization,
} from '@/lib/services/organizations/organizations.query'
import {
  getSalesTaxReportFiltersFromSearch,
  salesTaxReportSearchSchema,
  type SalesTaxReportFilters,
  type SalesTaxReportSearch,
} from '@/lib/services/reports/reports.schema'
import {
  reportQueries,
  useExportSalesTaxReportMutation,
  useSalesTaxReport,
} from '@/lib/services/reports/reports.query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Download, ReceiptText } from 'lucide-react'
import { startTransition } from 'react'
import { toast } from 'sonner'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { downloadExportedReport, getExportErrorMessage } from '../-helpers'
import { SalesTaxReportResults } from './-results'

export const Route = createFileRoute('/_authenticated/$orgId/reports/sales-tax/')({
  component: SalesTaxReportPage,
  validateSearch: (search) => salesTaxReportSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)) {
      return
    }

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))
    const filters = getReportFilters(search, organization?.timezone)

    // An incomplete or failed report shows inline on the page instead of failing the route.
    await context.queryClient.prefetchQuery(
      reportQueries.salesTax({
        organizationId: params.orgId,
        filters,
      })
    )
  },
})

export function SalesTaxReportPage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const { data: organization } = useOrganization(orgId)
  const filters = getReportFilters(search, organization?.timezone)
  const {
    data: report,
    error,
    isError,
    isPending,
    isFetching,
    refetch,
  } = useSalesTaxReport(filters, { retryOnMount: false })
  const showReport = canDisplaySalesReport({ data: report, error, isError })
  const exportMutation = useExportSalesTaxReportMutation()
  const organizationNow = getOrganizationNow(organization?.timezone)
  const datePickerBounds = getOrganizationReportDateBounds(
    organization?.created_at,
    organizationNow,
    organization?.timezone
  )
  const hasExportData =
    showReport && ((report?.jurisdictions.length ?? 0) > 0 || (report?.rows.length ?? 0) > 0)

  const applyFilters = ({ start_date, end_date, date_mode }: ReportDateRangeSelection) => {
    startTransition(() => {
      void navigate({
        ...path.salesTax.index().using({ orgId }).link,
        search: {
          start_date,
          end_date,
          date_mode: date_mode ?? undefined,
        },
        replace: Boolean(search.start_date && search.end_date) || Boolean(search.date_mode),
      })
    })
  }

  const handleExport = async () => {
    try {
      const response = await exportMutation.mutateAsync({
        organizationId: orgId,
        filters,
      })

      downloadExportedReport(
        response,
        `sales-tax-report-${filters.start_date}-to-${filters.end_date}.xlsx`
      )
    } catch (error) {
      toast.error(await getExportErrorMessage(error, 'Failed to export sales tax report'))
    }
  }

  return (
    <div className="pb-6">
      <AdminPageHeader
        icon={ReceiptText}
        title="Sales Tax Report"
        className="mb-4 mt-2"
        actions={
          <>
            <ReportDateRangeFilterBar
              appliedDateMode={REPORT_DATE_MODE.CUSTOM}
              initialFilters={{
                ...filters,
                ...datePickerBounds,
              }}
              now={organizationNow}
              onApply={applyFilters}
            />
            {hasExportData ? (
              <AdminHeaderActionButton
                type="button"
                variant="outline"
                onClick={handleExport}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4" />
                Export Spreadsheet
              </AdminHeaderActionButton>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <AdminHeaderActionButton type="button" variant="outline" disabled>
                      <Download className="h-4 w-4" />
                      Export Spreadsheet
                    </AdminHeaderActionButton>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Nothing to export.</TooltipContent>
              </Tooltip>
            )}
          </>
        }
      />

      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          hasCachedReport={showReport}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading sales tax…" />
      ) : showReport ? (
        <SalesTaxReportResults report={report} orgId={orgId} path={path} />
      ) : null}
    </div>
  )
}

function getReportFilters(
  search: SalesTaxReportSearch,
  organizationTimezone: string
): SalesTaxReportFilters {
  const defaultFilters = getCurrentReportDateFilters(getOrganizationNow(organizationTimezone))

  return getSalesTaxReportFiltersFromSearch(search, defaultFilters)
}
